import { lazy, Suspense, type ReactNode } from "react";
import { LoadingState } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { BASE_MODULE, moduleForPath } from "./moduleMap.js";
import { useModules } from "./useModules.js";

/**
 * Nur nachladen, wenn wirklich gesperrt wird. Der Waechter umhuellt JEDE
 * Route, haengt also am Startpfad; die Sperrseite zieht ueber `EmptyState`
 * Karte, Stack und Link mit und hat im Entry-Chunk nichts zu suchen
 * (`src/bundleBudget.test.ts`). Im Normalfall wird dieser Zweig nie geladen.
 */
const ModuleUnavailable = lazy(() =>
  import("./ModuleUnavailable.js").then((m) => ({ default: m.ModuleUnavailable })),
);

/**
 * A-MODUL-NAV1 (PASS 001699) — EIN Routenwaechter fuer modulgebundene Routen.
 *
 * Genau eine Stelle umhuellt alle Routen (`App.tsx`), damit kein Bereich
 * vergessen wird und kein zweiter Waechter entsteht. Der Waechter entscheidet
 * nur ueber Sichtbarkeit:
 *
 *   - Basis-Routen laufen sofort durch, ohne auf eine Abfrage zu warten.
 *   - Ausgeblendetes Modul -> neutrale Seite "nicht verfuegbar".
 *   - `read_only` laeuft durch; die Schreibsperren macht der Server (Lane C),
 *     der Client kennzeichnet das nur im Menue.
 *   - Fehler -> Seite wird gezeigt (fail-open, siehe `useModules.ts`).
 *
 * Der Lade-Zustand nutzt denselben `LoadingState` wie der Suspense-Fallback in
 * `App.tsx`, damit kein zweiter Ladebalken-Typ entsteht.
 */
export function ModuleRouteGuard({
  routePath,
  children,
}: {
  readonly routePath: string;
  readonly children: ReactNode;
}): ReactNode {
  const { t } = useLocale();
  const module = moduleForPath(routePath);
  // Basis ist immer sichtbar (Owner 05.10.) — kein Hook-Ergebnis abwarten.
  const modules = useModules();
  if (module === BASE_MODULE) return children;
  if (modules.loading) return <LoadingState label={t("common.loading")} />;
  if (modules.isHidden(module)) {
    return (
      <Suspense fallback={<LoadingState label={t("common.loading")} />}>
        <ModuleUnavailable module={module} />
      </Suspense>
    );
  }
  return children;
}
